import type { ContentSegment, VariableDefinition } from './types';

const VARIABLE_REGEX = /(\{\{[\w_]+\}\})/g;

/**
 * Parses content string into segments of text and variables
 */
export const parseContent = (content: string): ContentSegment[] => {
  return content
    .split(VARIABLE_REGEX)
    .filter(Boolean) // Remove empty parts produced by .split()
    .map((part): ContentSegment => {
      if (part.startsWith('{{') && part.endsWith('}}')) {
        return {
          type: 'variable',
          content: part,
          varName: part.slice(2, -2),
        };
      }
      return { type: 'text', content: part };
    });
};

/**
 * Gets the current value for a variable, using the value from content or defaultValue
 */
export const getVariableValue = (
  varName: string,
  variableValues: Record<string, string>,
  variableDefinitions: VariableDefinition[],
): string => {
  // If the user has already set a value for the variable, use that
  if (variableValues[varName]) {
    return variableValues[varName];
  }

  // If the user hasn't set a value for the variable, use the default value
  const definition = variableDefinitions.find((d) => d.varName === varName);
  // If no definition or default, return the variable name itself (e.g., "patient_name")
  return definition?.defaultValue ?? varName;
};

/**
 * Renders a template string by replacing {{variable}} placeholders with values from a map.
 * Shared utility for rendering templates with variable values.
 */
export const renderTemplate = (
  template: string,
  variableValues: Record<string, string>,
  fallbackValue: string = '___',
): string => {
  if (!template) return template;

  return template.replace(/\{\{([\w_]+)\}\}/g, (match, varName) => {
    return variableValues[varName] ?? fallbackValue;
  });
};

/**
 * Serializes the contentEditable DOM back to a string with {{variable}} syntax.
 * Manually traverses the DOM to preserve whitespace (innerText on detached elements
 * doesn't apply CSS white-space rules, causing spaces to collapse).
 */
export const serializeContent = (container: HTMLDivElement | null): string => {
  if (!container) return '';

  const result: string[] = [];

  const processNode = (node: Node): void => {
    // Text nodes: preserve text content exactly as-is
    if (node.nodeType === Node.TEXT_NODE) {
      result.push(node.textContent || '');
      return;
    }

    const el = node as HTMLElement;

    // Variable chips: convert back to {{variableName}} syntax
    if (el.dataset.variable) {
      result.push(`{{${el.dataset.variable}}}`);
      return;
    }

    // Line breaks: convert <br> to newline character
    if (el.tagName === 'BR') {
      result.push('\n');
      return;
    }

    // Recursively process all child nodes
    Array.from(el.childNodes).forEach(processNode);
  };

  // Process all top-level children of the container
  Array.from(container.childNodes).forEach(processNode);

  // Join all collected parts into final string
  return result.join('');
};
